import React, { useEffect, useMemo, useRef, useState } from "react";

import { useTranslation } from "react-i18next";

import moment from "moment-timezone";
import { Line } from "react-chartjs-2";
import { useForm, Controller } from "react-hook-form";
import Skeleton from "react-loading-skeleton";

import DateRangePickerDropdown from "@components/dateRange/DropdownDateRange";
import { DropdownDevise, DropdownYear } from "@components/tailwindComponents";
import Button from "@components/tailwindComponents/Button";

import { customTooltipsEvolution } from "./customOptions";

function SalaryEvolutionGraph({
  salaryEvolutions,
  applyClicked,
  setSelectedYearEvolution,
  setSelectedDeviseEvolution,
  setSelectedYearsEvolution,
  deviseList,
}) {
  const chartRef = useRef(null);
  const { t: statTranslator } = useTranslation("pages.employés.statistics");
  const [isReady, setIsReady] = useState(true);
  const { control } = useForm({
    reValidateMode: "onChange",
    mode: "onChange",
  });
  const [selectedType, setSelectedType] = useState("years");
  const { dataMonth, dataYears, filter } = salaryEvolutions;

  const lists =
    useMemo(
      () =>
        selectedType === "month"
          ? dataMonth?.data?.map((dataSet) => ({
              ...dataSet,
              data: dataSet.data?.map((_data) => ({
                x: statTranslator(_data.x),
                y: _data.y,
              })),
            }))
          : dataYears?.data,
      [dataMonth, dataYears, selectedType],
    ) || [];
  const dataLabels = useMemo(
    () =>
      selectedType === "month" && dataMonth?.labels?.length
        ? dataMonth?.labels.map((_month) => statTranslator(_month))
        : selectedType === "years" && dataYears?.labels?.length
          ? dataYears?.labels
          : [],
    [dataMonth, dataYears, selectedType],
  );
  const LINE_GRAPH_OPTIONS = {
    scales: {
      yAxes: [
        {
          ticks: {
            suggestedMin: 0, // Valeur minimale de l'intervalle de l'axe Y
            //suggestedMax: 10, // Valeur maximale de l'intervalle de l'axe Y
            stepSize: 10000, // Pas de l'axe Y
          },
        },
      ],
    },
    legend: {
      display: false,
    },
    spanGaps: true,
    tooltips: {
      enabled: false,
      custom: (tooltip) =>
        customTooltipsEvolution(tooltip, chartRef, statTranslator),
    },
  };

  const lineGraphData = () => {
    return {
      labels: [...dataLabels],
      datasets: [...lists],
    };
  };

  useEffect(() => {
    if (applyClicked) {
      setIsReady(false);
    } else {
      setIsReady(true);
    }
  }, [applyClicked]);
  const testValidation =
    filter?.employers?.length && lineGraphData().datasets?.length;
  return (
    <div className="arh-border arh-border-solid arh-border-borderStat arh-px-14 arh-py-6">
      <div className={`arh-flex arh-w-full arh-flex-col arh-space-y-4`}>
        <div
          className={`arh-flex arh-w-full ${!testValidation ? "arh-flex-col" : "arh-items-center arh-justify-between"}`}
        >
          <div className="arh-flex arh-items-center arh-space-x-4">
            <div className="arh-font-poppins arh-text-15 arh-font-bold arh-text-black">
              {statTranslator("Évolution salariale")}
            </div>
            {testValidation ? (
              <div className="arh-flex arh-items-center arh-justify-center arh-space-x-4">
                <div className="arh-w-24">
                  <DropdownDevise
                    control={control}
                    name="salary"
                    customButtonClass="arh-py-3"
                    defaultValue={deviseList?.find(
                      (_devise) =>
                        _devise._id === lineGraphData()?.datasets?.[0]?.devise,
                    )}
                    setSelectedDeviseEvolution={setSelectedDeviseEvolution}
                  />
                </div>
                {selectedType === "month" ? (
                  <div className="arh-w-24">
                    <DropdownYear
                      control={control}
                      name="year"
                      setSelectedYearEvolution={setSelectedYearEvolution}
                    />
                  </div>
                ) : (
                  <Controller
                    control={control}
                    name="years"
                    defaultValue={[
                      {
                        startDate: moment()
                          .subtract(5, "years")
                          .startOf("year")
                          .toDate(),
                        endDate: moment().endOf("day").toDate(),
                      },
                    ]}
                    rules={{
                      required: true,
                      validate: (period) =>
                        Boolean(period?.[0]?.startDate && period?.[0]?.endDate),
                    }}
                    render={({
                      field: { onChange, value, name },
                      fieldState: { invalid },
                    }) => (
                      <div className="datepicker_modals_parcours datePicker_posteAvailable blackSvgColor arh-year-range ">
                        <div className="dateRange_green position_relative DateRangePicker_customisation arh-year-range-green DateRangePicker_customisation--green">
                          <DateRangePickerDropdown
                            key="date_range_periode"
                            classParent="arhDatePicker"
                            className="input_simple focus_green"
                            textInput={statTranslator("Années")}
                            classSwitch="c-switch-pill-checked--green--color--to--left"
                            classBackground="green"
                            SaveCloseRange={(date) => {
                              onChange(date);
                              if (date?.[0].startDate && date?.[0].endDate)
                                setSelectedYearsEvolution([
                                  new Date(date?.[0].startDate).getFullYear(),
                                  new Date(date?.[0].endDate).getFullYear(),
                                ]);
                            }}
                            dateSelected={value?.[0]}
                            dateFormat="yyyy"
                            showMonthDropdown={false}
                            showYearDropdown={false}
                            showResetFilter={false}
                            showYearPicker
                            yearRange
                            cancelMode
                          />
                        </div>
                      </div>
                    )}
                  />
                )}
              </div>
            ) : (
              ""
            )}
          </div>
          {testValidation ? (
            <div className="arh-flex arh-items-center arh-justify-center arh-space-x-2">
              <div className="arh-w-24">
                <Button
                  variant={
                    selectedType === "years" ? "green" : "green-outlined"
                  }
                  onClick={() => setSelectedType("years")}
                >
                  {statTranslator("Vue années")}
                </Button>
              </div>
              <div className="arh-w-24">
                <Button
                  onClick={() => setSelectedType("month")}
                  variant={
                    selectedType === "month" ? "green" : "green-outlined"
                  }
                >
                  {statTranslator("Vue mois")}
                </Button>
              </div>
            </div>
          ) : (
            ""
          )}
        </div>
        {testValidation ? (
          <div className="arh-font-poppins arh-text-[11px] arh-font-normal arh-italic arh-text-[#859298]">
            (
            {statTranslator(
              "Veuillez cliquer sur le bouton 'Appliquer' en haut pour voir les changements",
            )}
            .)
          </div>
        ) : (
          ""
        )}
        {testValidation ? (
          <div className="arh-detail-canvasSvg arh-w-full">
            {isReady ? (
              <Line
                ref={chartRef}
                options={LINE_GRAPH_OPTIONS}
                data={() => lineGraphData()}
              />
            ) : (
              <Skeleton className="arh-h-96" />
            )}
          </div>
        ) : (
          <div className="arh-p-2 arh-font-poppins arh-text-xs arh-font-normal arh-text-black">
            {filter?.employers?.length && !lineGraphData().datasets?.length
              ? statTranslator("Aucun détail à afficher")
              : `${statTranslator(
                  "Veuillez sélectionner au moins un employé pour afficher les évolutions salariales comparatives",
                )} .`}
          </div>
        )}
      </div>
    </div>
  );
}

export default SalaryEvolutionGraph;
